import {
  type AlertDto,
  type AlertTransitionDto,
  NOTIFICATION_CHANNELS,
  NOTIFY_REASONS,
  type NotificationChannel,
  severityName,
} from '@vision360/contracts';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { absoluteTime, ago, formatNumber, SEVERITY_CLASS } from '@/widgets/_sdk/format';
import { AlertActions } from './alert-actions';
import { useAlertDetail } from './api';
import { StatusChip } from './status-chip';

/**
 * One alert (PRD VS-061/062): body, the context the rule recorded — shown as
 * raw data, never interpreted — and the transitions timeline from `GET /alerts/:id`.
 */

const REASONS = ['manual', 'cleared', 'idle', 'rule_deleted'] as const;
const SUPPRESSED = ['quiet_hours', 'not_configured'] as const;

const isChannel = (value: unknown): value is NotificationChannel =>
  NOTIFICATION_CHANNELS.some((c) => c === value);

/**
 * Step 32: `notified` and `delivery_failed` contexts read as French label/value pairs —
 * delivered channels, each silenced channel and why, or the channel that gave up. A value
 * the web does not know stays verbatim. Null for every other transition (shown raw).
 */
export function notificationFacts(
  t: TFunction<['alerts', 'rules']>,
  transition: AlertTransitionDto,
): [string, string][] | null {
  const ctx = transition.context;
  const channel = (c: NotificationChannel) => t(`rules:channels.${c}`);
  if (transition.type === 'notified') {
    const reason = NOTIFY_REASONS.find((r) => r === ctx['reason']);
    const delivered = Array.isArray(ctx['channels']) ? ctx['channels'].filter(isChannel) : [];
    const suppressed =
      typeof ctx['suppressed'] === 'object' && ctx['suppressed'] !== null
        ? Object.entries(ctx['suppressed'])
        : [];
    return [
      ...(reason
        ? [[t('alerts:notify.reason'), t(`alerts:notify.reasons.${reason}`)] as [string, string]]
        : []),
      [
        t('alerts:notify.delivered'),
        delivered.length > 0 ? delivered.map(channel).join(', ') : t('alerts:notify.none'),
      ],
      ...suppressed.map(([c, why]): [string, string] => {
        const known = SUPPRESSED.find((s) => s === why);
        return [
          isChannel(c) ? channel(c) : c,
          known ? t(`alerts:notify.suppressed.${known}`) : formatContextValue(why),
        ];
      }),
    ];
  }
  if (transition.type === 'delivery_failed') {
    const c = ctx['channel'];
    return [
      [t('alerts:notify.channel'), isChannel(c) ? channel(c) : formatContextValue(c)],
      [t('alerts:notify.attempts'), formatContextValue(ctx['attempts'])],
      [t('alerts:notify.error'), formatContextValue(ctx['error'])],
    ];
  }
  return null;
}
const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** A context value as data: dates in French, numbers grouped, the rest verbatim. */
export function formatContextValue(value: unknown): string {
  if (typeof value === 'string') {
    return ISO_DATE.test(value) && !Number.isNaN(Date.parse(value))
      ? absoluteTime.format(new Date(value))
      : value;
  }
  if (typeof value === 'number') return formatNumber(value, Number.isInteger(value) ? 0 : 2);
  return JSON.stringify(value);
}

function TimeValue({ iso, now }: { iso: string; now: number }) {
  return (
    <time dateTime={iso} title={ago(iso, now)} className="font-mono text-sm tabular-nums">
      {absoluteTime.format(new Date(iso))}
    </time>
  );
}

function Transition({ transition, last }: { transition: AlertTransitionDto; last: boolean }) {
  const { t } = useTranslation(['alerts', 'rules']);
  const facts = notificationFacts(t, transition);
  const silenced =
    transition.type === 'notified' &&
    !(Array.isArray(transition.context['channels']) && transition.context['channels'].length > 0);
  const entries = facts ?? Object.entries(transition.context);
  // Known resolve reasons in French; any other value stays as the engine wrote it.
  const reason = (value: unknown) => {
    const known = REASONS.find((r) => r === value);
    return known ? t(`alerts:reasons.${known}`) : formatContextValue(value);
  };
  return (
    <li className="relative pb-4 pl-6 last:pb-0">
      {!last && (
        <span aria-hidden="true" className="absolute top-3 bottom-0 left-[5px] w-px bg-border" />
      )}
      <span
        aria-hidden="true"
        className={cn(
          'absolute top-1.5 left-0 h-[11px] w-[11px] rounded-full border-2 border-card',
          last ? 'bg-primary ring-2 ring-primary/30' : 'bg-muted-foreground/50',
        )}
      />
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <span className="text-sm font-medium">
          {silenced
            ? t('alerts:transitions.notifiedSilenced')
            : t(`alerts:transitions.${transition.type}`)}
        </span>
        <time
          dateTime={transition.at}
          className="font-mono text-xs text-muted-foreground tabular-nums"
        >
          {absoluteTime.format(new Date(transition.at))}
        </time>
      </div>
      {entries.length > 0 && (
        <dl className="mt-1 flex flex-wrap gap-1.5">
          {entries.map(([key, value]) => (
            <div
              key={key}
              className="flex gap-1 rounded-md bg-muted/60 px-1.5 py-0.5 font-mono text-xs"
            >
              <dt className="text-muted-foreground">{key}</dt>
              <dd className="tabular-nums">
                {facts
                  ? String(value)
                  : key === 'reason'
                    ? reason(value)
                    : formatContextValue(value)}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  );
}

export function AlertDetail({
  alert: listed,
  meta,
  onClose,
  now = Date.now(),
}: {
  alert: AlertDto;
  /** "rule · source", already resolved by the page. */
  meta: string;
  onClose: () => void;
  now?: number;
}) {
  const { t } = useTranslation(['alerts', 'widgets']);
  const detail = useAlertDetail(listed.id);
  // The list row is enough to draw the top at once; the detail brings the timeline.
  const alert = detail.data?.alert ?? listed;
  const context = Object.entries(alert.context);
  const facts: [string, string | null][] = [
    [t('alerts:detail.opened'), alert.openedAt],
    [t('alerts:detail.lastFired'), alert.lastFiredAt],
    [t('alerts:detail.acked'), alert.ackedAt],
    [t('alerts:detail.snoozedUntil'), alert.status === 'snoozed' ? alert.snoozedUntil : null],
    [t('alerts:detail.resolved'), alert.resolvedAt],
  ];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        closeLabel={t('alerts:detail.close')}
        className="animate-enter max-w-2xl gap-5"
        onEscapeKeyDown={(e) => {
          // Esc inside the snooze durations folds them; the dialog stays.
          if (e.target instanceof Element && e.target.closest('[data-snooze-durations]')) {
            e.preventDefault();
          }
        }}
      >
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'rounded-sm px-1.5 py-0.5 text-xs font-medium',
                SEVERITY_CLASS[alert.severity],
              )}
            >
              {t(`widgets:severity.${severityName(alert.severity)}`)}
            </span>
            <StatusChip alert={alert} />
          </div>
          <DialogTitle className="text-xl leading-tight">{alert.title}</DialogTitle>
          <DialogDescription>{meta}</DialogDescription>
        </div>

        {alert.body && <p className="whitespace-pre-wrap text-sm leading-relaxed">{alert.body}</p>}

        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 border-y py-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs text-muted-foreground">{t('alerts:detail.fires')}</dt>
            <dd className="font-mono text-sm tabular-nums">{formatNumber(alert.fireCount)}</dd>
          </div>
          {facts.map(([label, iso]) =>
            iso ? (
              <div key={label}>
                <dt className="text-xs text-muted-foreground">{label}</dt>
                <dd>
                  <TimeValue iso={iso} now={now} />
                </dd>
              </div>
            ) : null,
          )}
        </dl>

        <section aria-labelledby="alert-context" className="space-y-2">
          <h3 id="alert-context" className="text-sm font-semibold">
            {t('alerts:detail.context')}
          </h3>
          {context.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('alerts:detail.contextEmpty')}</p>
          ) : (
            <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-1 rounded-lg bg-muted/50 p-3 font-mono text-xs">
              {context.map(([key, value]) => (
                <div key={key} className="contents">
                  <dt className="text-muted-foreground">{key}</dt>
                  <dd className="break-all tabular-nums">{formatContextValue(value)}</dd>
                </div>
              ))}
            </dl>
          )}
        </section>

        <section aria-labelledby="alert-timeline" className="space-y-3">
          <h3 id="alert-timeline" className="text-sm font-semibold">
            {t('alerts:detail.timeline')}
          </h3>
          {detail.isPending ? (
            <output
              aria-live="polite"
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              <Spinner />
              {t('alerts:detail.loading')}
            </output>
          ) : detail.isError ? (
            <Alert variant="destructive" role="alert">
              {t('alerts:detail.error')}
            </Alert>
          ) : detail.data.transitions.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('alerts:detail.timelineEmpty')}</p>
          ) : (
            <ol className="animate-rise">
              {detail.data.transitions.map((tr, i, all) => (
                <Transition key={tr.id} transition={tr} last={i === all.length - 1} />
              ))}
            </ol>
          )}
        </section>

        <AlertActions alert={alert} className="border-t pt-4" />
      </DialogContent>
    </Dialog>
  );
}
